import client from './client';
import {
  CandidateDocumentsResponse,
  CompanySuggestionsResponse,
  CompanySummary,
  PipelineIntelligenceOverview,
  PipelineSuggestion,
  PreviewRequest,
  PreviewResponse,
  ReviewDecisionResponse,
  ReviewHistoryResponse,
} from '../types/pipelineIntelligence';

const BASE = '/api/v1/pipeline-intelligence';

export const getPipelineIntelligenceOverview = async (): Promise<
  PipelineIntelligenceOverview
> => {
  const response = await client.get(`${BASE}/overview`);
  return response.data;
};

export const listPipelineIntelligenceCompanies = async (params?: {
  difference_type?: string;
  status?: string;
}): Promise<CompanySummary[]> => {
  const response = await client.get(`${BASE}/companies`, { params });
  return response.data;
};

export const getCompanySuggestions = async (
  companyId: string,
  params?: {
    difference_type?: string;
    status?: string;
    addable_only?: boolean;
  },
): Promise<CompanySuggestionsResponse> => {
  const response = await client.get(
    `${BASE}/companies/${encodeURIComponent(companyId)}/suggestions`,
    { params },
  );
  return response.data;
};

// The suggestion key contains pipes, so it travels as a query parameter
// rather than a path segment.
export const getSuggestion = async (
  suggestionKey: string,
): Promise<PipelineSuggestion> => {
  const response = await client.get(`${BASE}/suggestion`, {
    params: { suggestion_key: suggestionKey },
  });
  return response.data;
};

export const getReviewHistory = async (
  suggestionKey: string,
): Promise<ReviewHistoryResponse> => {
  const response = await client.get(`${BASE}/suggestion/history`, {
    params: { suggestion_key: suggestionKey },
  });
  return response.data;
};

export const rejectSuggestion = async (
  suggestionKey: string,
  reviewNote?: string,
): Promise<ReviewDecisionResponse> => {
  const response = await client.post(
    `${BASE}/suggestion/reject`,
    { review_note: reviewNote ?? null },
    { params: { suggestion_key: suggestionKey } },
  );
  return response.data;
};

/**
 * Approve a suggestion, creating canonical Therapeutic and DrugDevelopment
 * records. `indicationOverride` is required when the source stated no
 * indication — the backend refuses rather than inventing a disease.
 */
export const approveSuggestion = async (
  suggestionKey: string,
  options?: { reviewNote?: string; indicationOverride?: string },
): Promise<ReviewDecisionResponse> => {
  const response = await client.post(
    `${BASE}/suggestion/approve`,
    {
      review_note: options?.reviewNote ?? null,
      indication_override: options?.indicationOverride ?? null,
    },
    { params: { suggestion_key: suggestionKey } },
  );
  return response.data;
};

export const reopenSuggestion = async (
  suggestionKey: string,
  reviewNote?: string,
): Promise<ReviewDecisionResponse> => {
  const response = await client.post(
    `${BASE}/suggestion/reopen`,
    { review_note: reviewNote ?? null },
    { params: { suggestion_key: suggestionKey } },
  );
  return response.data;
};

export const getCandidateDocuments = async (
  companyId: string,
  params?: {
    source_type?: string;
    days_back?: number;
    document_limit?: number;
  },
): Promise<CandidateDocumentsResponse> => {
  const response = await client.get(
    `${BASE}/companies/${encodeURIComponent(companyId)}/candidate-documents`,
    { params },
  );
  return response.data;
};

export const runPipelineIntelligencePreview = async (
  companyId: string,
  payload: PreviewRequest,
): Promise<PreviewResponse> => {
  const response = await client.post(
    `${BASE}/companies/${encodeURIComponent(companyId)}/preview`,
    payload,
  );
  return response.data;
};
